/**
 * Utility functions for handling availability data
 */

/**
 * Groups availability slots by day and merges consecutive time slots
 * @param {Array} availabilitySlots - Array of availability slot objects
 * @returns {Object} Grouped availability by day name in correct order
 */
export const groupAndMergeAvailability = (availabilitySlots) => {
  if (!availabilitySlots || availabilitySlots.length === 0) {
    return {};
  }

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Group by day
  const groupedByDay = availabilitySlots.reduce((acc, slot) => {
    const dayName = dayNames[slot.day_of_week];
    if (!acc[dayName]) {
      acc[dayName] = [];
    }
    acc[dayName].push(slot);
    return acc;
  }, {});

  // Merge consecutive slots for each day and maintain order
  const mergedAvailability = {};
  dayNames.forEach((dayName) => {
    if (groupedByDay[dayName]) {
      mergedAvailability[dayName] = mergeConsecutiveSlots(groupedByDay[dayName]);
    }
  });

  return mergedAvailability;
};

/**
 * Merges consecutive time slots of the same availability type
 * @param {Array} slots - Array of availability slots for a single day
 * @returns {Array} Merged slots sorted by time and priority
 */
const mergeConsecutiveSlots = (slots) => {
  if (slots.length <= 1) return slots;

  // Sort by start time first, then by priority
  const sorted = slots.sort((a, b) => {
    const timeComparison = a.start_time.localeCompare(b.start_time);
    if (timeComparison !== 0) return timeComparison;
    return (
      getAvailabilityTypePriority(a.availability_type) -
      getAvailabilityTypePriority(b.availability_type)
    );
  });

  const merged = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];

    // Check if slots are consecutive and same type
    if (
      isConsecutive(current.end_time, next.start_time) &&
      current.availability_type === next.availability_type
    ) {
      // Merge the slots
      current.end_time = next.end_time;
    } else {
      // Start a new block
      merged.push(current);
      current = { ...next };
    }
  }
  merged.push(current);

  // Final sort by start time and priority
  return merged.sort((a, b) => {
    const timeComparison = a.start_time.localeCompare(b.start_time);
    if (timeComparison !== 0) return timeComparison;
    return (
      getAvailabilityTypePriority(a.availability_type) -
      getAvailabilityTypePriority(b.availability_type)
    );
  });
};

/**
 * Checks if two time strings are consecutive (one ends when the other starts)
 * @param {string} endTime - End time in HH:MM format
 * @param {string} startTime - Start time in HH:MM format
 * @returns {boolean} True if times are consecutive
 */
const isConsecutive = (endTime, startTime) => {
  return endTime === startTime;
};

/**
 * Formats a time range for display
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {string} Formatted time range
 */
export const formatTimeRange = (startTime, endTime) => {
  const formatTime = (time) => {
    return time.replace(':00', '');
  };

  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

/**
 * Gets the display name for availability type
 * @param {string} availabilityType - The availability type
 * @returns {string} Display name
 */
export const getAvailabilityTypeDisplay = (availabilityType) => {
  switch (availabilityType) {
    case 'preferred':
      return 'Preferred';
    case 'available':
      return 'Available';
    default:
      return availabilityType?.charAt(0).toUpperCase() + availabilityType?.slice(1) || 'Unknown';
  }
};

/**
 * Gets the CSS classes for availability type badge
 * @param {string} availabilityType - The availability type
 * @returns {string} CSS classes
 */
export const getAvailabilityTypeBadgeClasses = (availabilityType) => {
  switch (availabilityType) {
    case 'preferred':
      return 'bg-green-100 text-green-800';
    case 'available':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Gets the priority order for availability types (for sorting)
 * @param {string} availabilityType - The availability type
 * @returns {number} Priority order (lower number = higher priority)
 */
export const getAvailabilityTypePriority = (availabilityType) => {
  switch (availabilityType) {
    case 'preferred':
      return 1;
    case 'available':
      return 2;
    default:
      return 3;
  }
};

/**
 * Counts availability from merged slots to ensure consistent counting
 * This prevents double-counting when a user has both preferred and available for the same time
 * @param {Array} mergedSlots - Array of merged availability slots
 * @returns {Object} Counts by day and time slot
 */
export const countAvailabilityFromMergedSlots = (mergedSlots) => {
  const countsMap = {};

  if (!mergedSlots || mergedSlots.length === 0) {
    return countsMap;
  }

  mergedSlots.forEach((slot) => {
    const dayOfWeek = slot.day_of_week;
    const startTime = slot.start_time;
    const endTime = slot.end_time;
    const availabilityType = slot.availability_type;

    // Generate 30-minute time slots between start and end
    const currentTime = new Date(`2000-01-01T${startTime}`);
    const endDateTime = new Date(`2000-01-01T${endTime}`);

    while (currentTime < endDateTime) {
      const timeSlot = currentTime.toTimeString().slice(0, 5); // HH:MM format
      const key = `${dayOfWeek}-${timeSlot}`;

      if (!countsMap[key]) {
        countsMap[key] = { available: 0, preferred: 0 };
      }

      if (availabilityType === 'preferred') {
        countsMap[key].preferred += 1;
      } else if (availabilityType === 'available') {
        countsMap[key].available += 1;
      }

      // Move to next 30-minute slot
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }
  });

  return countsMap;
};

/** Parse "HH:MM" or "HH:MM:SS" for a fixed calendar date (used only for time arithmetic). */
function parseTimeOnRefDay(timeStr) {
  const t = String(timeStr ?? '').trim();
  if (!t) return new Date('invalid');
  const iso = t.length === 5 ? `${t}:00` : t;
  return new Date(`2000-01-01T${iso}`);
}

/**
 * Turns API weekly availability rows into the grid shape used by AvailabilityGrid:
 * `{ [dayIndex]: { 'HH:MM': availability_type } }`.
 *
 * Rows may span multiple hours (e.g. 06:00–10:00). The UI grid only renders rows listed in
 * `allowedHalfHourStarts` (typically TIME_SLOTS starting at 07:00). Without expanding the
 * interval into those half-hour start keys, only `06:00` would be set — which is not a grid row —
 * and the calendar looks empty.
 *
 * @param {Array<{ day_of_week: number, start_time: string, end_time: string, availability_type: string }>} slots
 * @param {string[]} allowedHalfHourStarts e.g. TIME_SLOTS from AvailabilityTypes
 */
export function weeklyApiSlotsToGridAvailability(slots, allowedHalfHourStarts) {
  const gridData = {};
  const allowed = new Set(allowedHalfHourStarts || []);

  if (!slots || !slots.length || allowed.size === 0) {
    return gridData;
  }

  for (const slot of slots) {
    const dayIndex = slot.day_of_week;
    const availabilityType = slot.availability_type;
    const start = parseTimeOnRefDay(slot.start_time);
    const end = parseTimeOnRefDay(slot.end_time);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) {
      continue;
    }

    const currentTime = new Date(start);
    while (currentTime < end) {
      const timeSlot = currentTime.toTimeString().slice(0, 5);
      if (allowed.has(timeSlot)) {
        if (!gridData[dayIndex]) {
          gridData[dayIndex] = {};
        }
        gridData[dayIndex][timeSlot] = availabilityType;
      }
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }
  }

  return gridData;
}
